import type { MouseEvent } from "react";
import { useFavorites } from "@/hooks/useFavorites";

interface FavoriteButtonProps {
  movieId: string;
  variant?: "icon" | "full" | "compact";
  className?: string;
}

export default function FavoriteButton({
  movieId,
  variant = "icon",
  className = "",
}: FavoriteButtonProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const active = isFavorite(movieId);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    toggleFavorite(movieId);
  };

  if (variant === "full") {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-pressed={active}
        className={`inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border px-4 py-2.5 text-sm font-medium transition-colors ${
          active
            ? "border-accent-500/60 bg-accent-500/15 text-accent-300"
            : "border-background-300 bg-background-100 text-foreground-200 hover:border-accent-500/50 hover:text-accent-300"
        } ${className}`}
      >
        <i className={`${active ? "ri-heart-3-fill" : "ri-heart-3-line"} text-base`} />
        {active ? "已收藏" : "收藏影片"}
      </button>
    );
  }

  if (variant === "compact") {
    return (
      <button
        type="button"
        onClick={handleClick}
        aria-pressed={active}
        className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs transition-colors ${
          active
            ? "bg-accent-500/15 text-accent-300"
            : "bg-background-200/80 text-foreground-300 hover:text-accent-300"
        } ${className}`}
      >
        <i className={`${active ? "ri-heart-3-fill" : "ri-heart-3-line"} text-sm`} />
        {active ? "已收藏" : "收藏"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={active ? "取消收藏" : "收藏影片"}
      aria-pressed={active}
      className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-md backdrop-blur-md transition-colors ${
        active
          ? "bg-accent-500/90 text-background-50"
          : "bg-background-950/60 text-foreground-100 hover:bg-accent-500/90 hover:text-background-50"
      } ${className}`}
    >
      <i className={`${active ? "ri-heart-3-fill" : "ri-heart-3-line"} text-base`} />
    </button>
  );
}